import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft, CreditCard as Edit, Mail, Phone, CheckCircle, Shield, MapPin, DollarSign, Clock, Award, Calendar, Image as ImageIcon, User } from 'lucide-react';
import { Logo } from '../components/Logo';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { CreateProviderProfileModal } from '../components/CreateProviderProfileModal';
import { ImageWithFallback } from '../components/ImageWithFallback';
import { useAuth } from '../context/AuthContext';
import { getProviderProfile, updateProviderProfile } from '../services/api';
import { ProviderProfile as ProviderProfileType, ProviderProfileFormData } from '../types';
import { ROUTES } from '../constants';
import { formatCoordinates } from '../utils/locationUtils';

export const ProviderProfile: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (user?.role === 'admin') {
    return <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />;
  }

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<ProviderProfileType | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, [user?.userId]);

  const fetchProfile = async () => {
    if (!user?.userId) {
      setError('User not found');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await getProviderProfile(user.userId);
      if (response.success && response.provider) {
        setProfile(response.provider);
      } else {
        setError(response.message || 'Failed to load profile');
      }
    } catch (err) {
      setError('Failed to load profile');
      console.error('Error fetching profile:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (formData: ProviderProfileFormData): Promise<{ success: boolean; error?: string }> => {
    if (!user?.userId) return { success: false, error: 'User not found' };

    const response = await updateProviderProfile(user.userId, {
      name: formData.name,
      phone: formData.phone,
      address: formData.address,
      postalCode: formData.postalCode,
      city: formData.city,
      latitude: formData.latitude,
      longitude: formData.longitude,
      typeOfCare: formData.typeOfCare,
      location: formData.location,
      serviceRadiusKm: formData.serviceRadiusKm,
      services: formData.services,
      pricing: formData.pricing,
      availability: formData.availability,
      credentials: formData.credentials,
      avatar: formData.avatar,
      pictures: formData.pictures,
    });

    if (response.success) {
      return { success: true };
    }

    return {
      success: false,
      error: response.error || 'Failed to update profile'
    };
  };

  const handleEditSuccess = async () => {
    setShowEditModal(false);
    await fetchProfile();
  };

  const formatAvailability = () => {
    if (!profile?.availability) return 'Not specified';

    const days = Object.entries(profile.availability);
    if (days.length === 0) return 'Not specified';

    return days.map(([day, hours]) => (
      <div key={day} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
        <span className="text-gray-700 capitalize font-medium">{day}</span>
        <span className="text-gray-600">{hours}</span>
      </div>
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Logo size="md" />
            </div>
          </div>
        </header>
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <LoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50">
        <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Logo size="md" />
            </div>
          </div>
        </header>
        <div className="flex flex-col items-center justify-center min-h-[calc(100vh-64px)] px-4">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Profile Not Found</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => navigate(ROUTES.SETTINGS)}>
              Back to Settings
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Logo size="md" />
            <Button
              variant="ghost"
              onClick={() => navigate(ROUTES.SETTINGS)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to Settings</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Provider Profile</h1>
            <p className="text-gray-600">View and manage your professional information</p>
          </div>
          <Button
            variant="primary"
            onClick={() => setShowEditModal(true)}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
          >
            <Edit className="h-4 w-4" />
            Edit Profile
          </Button>
        </div>

        <div className="space-y-6">
          <Card className="p-6">
            <div className="flex items-start gap-6 mb-6">
              <div className="flex-shrink-0">
                {profile.avatar ? (
                  <ImageWithFallback
                    src={profile.avatar}
                    alt={profile.name}
                    className="w-24 h-24 rounded-full object-cover border-4 border-green-100"
                    fallbackIcon={<User className="h-12 w-12 text-gray-400" />}
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center border-4 border-green-100">
                    <User className="h-12 w-12 text-gray-400" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{profile.name}</h2>
                <div className="flex items-center gap-2 text-gray-600 mb-2">
                  <Mail className="h-4 w-4" />
                  <span>{profile.email}</span>
                </div>
                {profile.phone && (
                  <div className="flex items-center gap-2 text-gray-600">
                    <Phone className="h-4 w-4" />
                    <span>{profile.phone}</span>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                {profile.isVerified && (
                  <Badge variant="success" className="flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Verified
                  </Badge>
                )}
                <Badge variant="primary">Provider</Badge>
              </div>
            </div>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Shield className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-semibold text-gray-900">Care Types</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {Array.isArray(profile.typeOfCare) ? (
                  profile.typeOfCare.map((type, index) => (
                    <Badge key={index} variant="primary">{type}</Badge>
                  ))
                ) : (
                  <Badge variant="primary">{profile.typeOfCare}</Badge>
                )}
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-semibold text-gray-900">Location</h3>
              </div>
              <div className="space-y-2">
                {profile.address && (
                  <p className="text-gray-700">{profile.address}</p>
                )}
                {(profile.city || profile.postalCode) && (
                  <p className="text-gray-700">
                    {profile.city}{profile.city && profile.postalCode ? ', ' : ''}{profile.postalCode}
                  </p>
                )}
                {!profile.address && !profile.city && !profile.postalCode && (
                  <p className="text-gray-700 text-lg">{profile.location}</p>
                )}
                {(profile.latitude !== undefined && profile.longitude !== undefined) && (
                  <p className="text-sm text-gray-500">
                    Coordinates: {formatCoordinates(profile.latitude, profile.longitude)}
                  </p>
                )}
                <p className="text-sm text-gray-600 pt-2 border-t border-gray-100">
                  Service Radius: {profile.serviceRadiusKm} miles
                </p>
              </div>
            </Card>
          </div>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="h-5 w-5 text-green-600" />
              <h3 className="text-lg font-semibold text-gray-900">Pricing</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-green-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-1">Hourly Rate</p>
                <p className="text-2xl font-bold text-gray-900">£{profile.pricing.hourly}</p>
              </div>
              {profile.pricing.daily && (
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Daily Rate</p>
                  <p className="text-2xl font-bold text-gray-900">£{profile.pricing.daily}</p>
                </div>
              )}
              {profile.pricing.monthly && (
                <div className="bg-green-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Monthly Rate</p>
                  <p className="text-2xl font-bold text-gray-900">£{profile.pricing.monthly}</p>
                </div>
              )}
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-5 w-5 text-green-600" />
              <h3 className="text-lg font-semibold text-gray-900">Services Offered</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {profile.services.map((service, index) => (
                <Badge key={index} variant="primary" className="bg-green-100 text-green-800">
                  {service}
                </Badge>
              ))}
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Award className="h-5 w-5 text-green-600" />
              <h3 className="text-lg font-semibold text-gray-900">Credentials</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {profile.credentials.map((credential, index) => (
                <Badge key={index} variant="success" className="bg-blue-100 text-blue-800">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  {credential}
                </Badge>
              ))}
            </div>
          </Card>

          {profile.gallery && profile.gallery.length > 0 && (
            <Card className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <ImageIcon className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-semibold text-gray-900">Gallery</h3>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {profile.gallery.map((image, index) => (
                  <div key={index} className="relative group overflow-hidden rounded-lg">
                    <ImageWithFallback
                      src={image}
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-32 object-cover transition-transform duration-300 group-hover:scale-110 rounded-lg"
                      fallbackIcon={<ImageIcon className="h-8 w-8 text-gray-400" />}
                    />
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="h-5 w-5 text-green-600" />
              <h3 className="text-lg font-semibold text-gray-900">Availability</h3>
            </div>
            <div className="space-y-1">
              {formatAvailability()}
            </div>
          </Card>

          <Card className="p-6 bg-gray-50">
            <div className="text-sm text-gray-600">
              <p className="mb-1">
                <span className="font-medium">Member Since:</span>{' '}
                {new Date(profile.createdAt).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
              <p>
                <span className="font-medium">Last Updated:</span>{' '}
                {new Date(profile.updatedAt).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
            </div>
          </Card>
        </div>
      </main>

      <CreateProviderProfileModal
        isOpen={showEditModal}
        providerId={user?.userId || ''}
        onSuccess={handleEditSuccess}
        onSubmit={handleEditSubmit}
        mode="edit"
        initialData={profile}
        onClose={() => setShowEditModal(false)}
      />
    </div>
  );
};
